import React, { useEffect, useState } from 'react';
import {
  Box, Button, Flex, Skeleton, Text,
} from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { LuCheck, LuChevronLeft } from 'react-icons/lu';
import useReviewCount from '../../hooks/useReviewCount';
import { getAccountDisplay, getPrimaryText } from '../../utils/movementDisplay';
import { formatRowAmount } from '../../utils/formatters';
import PhoneSheet, { usePhoneSheet } from './PhoneSheet';
import PhoneMovementRow from './PhoneMovementRow';
import { CategorySheet, CategoryTile } from './PhonePickers';
import { usePhoneShell } from './PhoneShellContext';
import ReviewFocus, { OtherTile } from './ReviewFocus';
import {
  ReasonLine, ReviewDisc, WaitingRow, reviewDateLabel, reviewFix, useCategorySuggestions, useReviewQueue,
} from './reviewFixes';

const CARD_TILES = 3;
// "Arrived today" is a glance, not a list to work through: a big import shows
// its newest few, the rest one tap away.
const ARRIVED_SHOWN = 10;

function SectionTitle({ children }) {
  return (
    <Text
      as="h3"
      px="16px"
      pt="18px"
      pb="8px"
      fontSize="12px"
      lineHeight="16px"
      fontWeight="600"
      letterSpacing="0.05em"
      textTransform="uppercase"
      color="phone.muted"
    >
      {children}
    </Text>
  );
}

function TextAction({ children, onClick, color = 'phone.link', isDisabled }) {
  return (
    <Button
      variant="ghost"
      color={color}
      fontSize="16px"
      fontWeight="600"
      minH="44px"
      px="12px"
      isDisabled={isDisabled}
      _hover={{ bg: 'transparent' }}
      _active={{ bg: 'redesign.bg.hover' }}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

/**
 * One movement to fix: what it is, the reason written out, and its fix as
 * visible buttons. Without the right to edit, only Open and Later.
 */
function ReviewCard({
  movement, queue, snoozed, busy, onFix, onPickCategory, onOpen, onLater, onUnsnooze,
}) {
  const { t } = useTranslation('movements');
  const reasons = queue.reasonsOf(movement);
  const fix = reviewFix(movement, reasons, queue.accounts);
  const canFix = queue.canFix && !snoozed;
  const { tiles, recentIds } = useCategorySuggestions(
    canFix && fix.kind === 'category' ? movement : null,
    queue.categories,
    CARD_TILES,
    queue.noteHistory,
  );

  let primary = null;
  if (canFix && fix.kind === 'switch') {
    primary = (
      <Button variant="phonePrimary" h="44px" fontSize="16px" px="16px" isDisabled={busy} onClick={() => onPickCategory(movement, fix.to, recentIds)}>
        {t(fix.to === 'Income' ? 'review.actions.switchToIncome' : 'review.actions.switchToExpense')}
      </Button>
    );
  } else if (canFix && fix.kind === 'move') {
    primary = (
      <Button
        variant="phonePrimary"
        h="44px"
        fontSize="16px"
        px="16px"
        maxW="100%"
        isDisabled={busy}
        onClick={() => onFix(movement, { account_id: fix.account.id }, t('review.toasts.moved', { account: fix.account.name }))}
      >
        <Box as="span" overflow="hidden" textOverflow="ellipsis">{t('review.actions.moveTo', { account: fix.account.name })}</Box>
      </Button>
    );
  }

  return (
    <Box
      mx="16px"
      mt="10px"
      pt="14px"
      // The text buttons carry their own air; a filled one needs the card's.
      pb={primary ? '14px' : '4px'}
      px="16px"
      borderRadius="16px"
      bg="redesign.bg.card"
      border="1px solid"
      borderColor="redesign.border"
      opacity={snoozed ? 0.75 : 1}
    >
      <Box display="grid" gridTemplateColumns="40px minmax(0, 1fr)" columnGap="12px" alignItems="center">
        <ReviewDisc movement={movement} />
        <Box minW={0}>
          {/* A long amount wraps at its code rather than squeezing the name away. */}
          <Flex justify="space-between" gap="10px" align="baseline">
            <Text textStyle="phoneRowName" color="redesign.heading" isTruncated minW={0}>{getPrimaryText(movement)}</Text>
            <Text textStyle="phoneRowAmount" color="redesign.heading" textAlign="right" flexShrink={0} maxW="60%">{formatRowAmount(movement, queue.home).text}</Text>
          </Flex>
          <Flex textStyle="phoneMeta" color="phone.muted" minW={0}>
            <Box as="span" minW={0} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{getAccountDisplay(movement)}</Box>
            <Box as="span" flexShrink={0} whiteSpace="pre">{` · ${reviewDateLabel(movement.date, t)}`}</Box>
          </Flex>
        </Box>
      </Box>

      <Flex direction="column" gap="4px" mt="8px">
        {reasons.map((r) => <ReasonLine key={r.flag} text={r.text} />)}
      </Flex>

      {canFix && fix.kind === 'category' && (
        <Flex
          gap="7px"
          mt="10px"
          overflowX="auto"
          mx="-16px"
          px="16px"
          sx={{
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            '& > *': { flex: '1 0 72px' },
            // A long name breaks at a hyphen, never mid-letter. Its language
            // is the user's data, so the page's own is used.
            '& span': { hyphens: 'auto', wordBreak: 'normal', overflowWrap: 'break-word' },
          }}
        >
          {tiles.map((c) => (
            <CategoryTile
              key={c.id}
              category={c}
              onClick={() => onFix(movement, { category_id: c.id }, t('review.toasts.categorySet', { name: c.name }))}
            />
          ))}
          <OtherTile label={t('review.actions.other')} onClick={() => onPickCategory(movement, movement.type, recentIds)} />
        </Flex>
      )}

      <Flex align="center" justify="space-between" flexWrap="wrap" gap="4px" mt={primary ? '10px' : '4px'} mr="-12px" ml={primary ? 0 : '-12px'}>
        {primary}
        {!primary && (
          snoozed
            ? <TextAction color="redesign.text" onClick={() => onUnsnooze(movement.id)}>{t('review.actions.reviewNow')}</TextAction>
            : <TextAction color="redesign.text" onClick={() => onLater(movement.id)}>{t('review.actions.later')}</TextAction>
        )}
        <Flex ml="auto">
          {primary && !snoozed && (
            <TextAction color="redesign.text" onClick={() => onLater(movement.id)}>{t('review.actions.later')}</TextAction>
          )}
          <TextAction onClick={() => onOpen(movement.id)}>{t('review.actions.open')}</TextAction>
        </Flex>
      </Flex>
    </Box>
  );
}

const MotionBox = motion(Box);

function CardList({ items, render }) {
  return (
    <AnimatePresence initial={false}>
      {items.map((m) => (
        <MotionBox
          key={m.id}
          layout
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, height: 0, transition: { duration: 0.25 } }}
          overflow="hidden"
        >
          {render(m)}
        </MotionBox>
      ))}
    </AnimatePresence>
  );
}

function HomeButton({ label }) {
  const { close } = usePhoneSheet();
  return (
    <Button
      variant="ghost"
      color="phone.link"
      fontSize="17px"
      fontWeight="500"
      px="4px"
      minH="44px"
      leftIcon={<LuChevronLeft size={24} />}
      iconSpacing="0"
      onClick={() => close()}
    >
      {label}
    </Button>
  );
}

/**
 * Review, Home's inbox: every movement that needs the user, each with its
 * reason and fix, then "Later", what is only waiting for the bank, and what
 * arrived today. "Review one by one" opens the focused view.
 */
export default function PhoneReviewSheet({ isOpen, onClose }) {
  const { t } = useTranslation('movements');
  const { openMovement } = usePhoneShell();
  const queue = useReviewQueue(isOpen);
  const { fixable, waiting, loading: countLoading } = useReviewCount();
  const [busy, setBusy] = useState(false);
  const [allArrived, setAllArrived] = useState(false);
  useEffect(() => { if (!isOpen) setAllArrived(false); }, [isOpen]);
  const [picker, setPicker] = useState(null);   // { movement, type, recentIds }
  const [focusOpen, setFocusOpen] = useState(false);

  const fix = async (movement, payload, message) => {
    if (busy) return;
    setBusy(true);
    await queue.applyFix(movement, payload, message);
    setBusy(false);
  };

  const pickCategory = (category) => {
    if (!category || !picker) return;
    const { movement, type } = picker;
    if (type !== movement.type) {
      fix(movement, { type, category_id: category.id },
        t(type === 'Income' ? 'review.toasts.switchedIncome' : 'review.toasts.switchedExpense', { name: category.name }));
    } else {
      fix(movement, { category_id: category.id }, t('review.toasts.categorySet', { name: category.name }));
    }
  };

  const card = (snoozed) => (m) => (
    <ReviewCard
      movement={m}
      queue={queue}
      snoozed={snoozed}
      busy={busy}
      onFix={fix}
      onPickCategory={(movement, type, recentIds) => setPicker({ movement, type, recentIds })}
      onOpen={openMovement}
      onLater={queue.snooze}
      onUnsnooze={queue.unsnooze}
    />
  );

  const subtitle = [
    t('review.subtitle.toFix', { count: fixable }),
    waiting > 0 && t('review.subtitle.waiting', { count: waiting }),
  ].filter(Boolean).join(' · ');

  const empty = !queue.loading && queue.fixable.length === 0;

  return (
    <PhoneSheet
      id="review"
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      title={t('review.title')}
      headerLeft={<HomeButton label={t('navigation:tabs.home')} />}
      headerRight={null}
    >
      {countLoading ? (
        <Box px="16px" pt="2px" h="22px">
          <Skeleton h="14px" w="45%" mt="3px" borderRadius="4px" startColor="redesign.bg.card" endColor="redesign.border" />
        </Box>
      ) : (
        <Text px="16px" pt="2px" fontSize="13px" lineHeight="20px" color="phone.muted">{subtitle}</Text>
      )}

      {queue.active.length > 0 && (
        <Box px="16px" mt="12px">
          <Button variant="phonePrimary" w="100%" onClick={() => setFocusOpen(true)}>
            {t('review.oneByOne', { count: queue.active.length })}
          </Button>
        </Box>
      )}

      {queue.loading && (
        <Box px="16px" mt="12px">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} h="150px" mt="10px" borderRadius="16px" startColor="redesign.bg.card" endColor="redesign.border" />
          ))}
        </Box>
      )}

      {empty && (
        <Flex direction="column" align="center" textAlign="center" px="16px" pt="32px" pb="8px">
          <Flex w="56px" h="56px" borderRadius="full" bg="rgba(21, 128, 61, 0.10)" _dark={{ bg: 'rgba(74, 222, 128, 0.14)' }} color="phone.income" align="center" justify="center">
            <Box as={LuCheck} boxSize="28px" />
          </Flex>
          <Text mt="12px" textStyle="phoneTitle" color="redesign.heading">{t('review.nothing')}</Text>
        </Flex>
      )}

      <CardList items={queue.active} render={card(false)} />

      {queue.later.length > 0 && (
        <>
          <SectionTitle>{t('review.laterTitle', { count: queue.later.length })}</SectionTitle>
          <CardList items={queue.later} render={card(true)} />
        </>
      )}

      {queue.waiting.length > 0 && (
        <>
          <SectionTitle>{t('review.waitingTitle', { count: queue.waiting.length })}</SectionTitle>
          {queue.waiting.map((m) => (
            <WaitingRow key={m.id} movement={m} home={queue.home} onClick={() => openMovement(m.id)} />
          ))}
          <Text px="16px" pt="10px" fontSize="13px" lineHeight="1.4" color="phone.muted">{t('review.waitingNote')}</Text>
        </>
      )}

      {queue.arrived.length > 0 && (
        <>
          <SectionTitle>{t('review.arrivedTitle', { count: queue.arrived.length })}</SectionTitle>
          {(allArrived ? queue.arrived : queue.arrived.slice(0, ARRIVED_SHOWN)).map((m) => (
            <PhoneMovementRow key={m.id} movement={m} homeCurrency={queue.home} onClick={() => openMovement(m.id)} />
          ))}
          {!allArrived && queue.arrived.length > ARRIVED_SHOWN && (
            <Box px="4px">
              <TextAction onClick={() => setAllArrived(true)}>
                {t('review.arrivedShowAll', { count: queue.arrived.length })}
              </TextAction>
            </Box>
          )}
        </>
      )}

      <CategorySheet
        isOpen={Boolean(picker)}
        onClose={() => setPicker(null)}
        categories={queue.categories}
        type={picker?.type}
        // The movement's own category, so the sheet never ticks a false "None".
        value={picker?.movement?.category_id ?? null}
        recentIds={picker?.type === picker?.movement?.type ? picker?.recentIds : []}
        onChange={pickCategory}
      />

      <ReviewFocus
        isOpen={focusOpen}
        onClose={() => setFocusOpen(false)}
        // Closing Review steps back over both sheets at once.
        onBackHome={onClose}
        queue={queue}
      />
    </PhoneSheet>
  );
}
